import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { ConfigureAgents as ConfigureAgentsLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { AgentDTO, PartitionAdminDTO } from '../../../NonReact/Types/Types'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { HIDE } from '../../../NonReact/constants/class-names'
import { isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { FormProvider, useForm, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { IconName } from '../../Common/Components/Icon'
import Tabs from '../../Common/Components/Tabs'
import { Tab } from '../../Common/Components/Tabs/Types'
import { ON_CHANGE } from '../../Common/Constants/HookFormEventNames'
import { LanguageContext } from '../../Common/Contexts'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { ModalContext, ModalContextType } from '../../Common/Contexts/ModalContext'
import UserInfoContext, { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { ReactState } from '../../Common/Types/Types'
import { FooterActions } from '../Actions/FooterActions'
import { CallCentreLicenceTypes } from '../Constants/CallCentreLicenceTypes'
import { AGENT_NUMBER_FIELD_NAME, PARTITION_FIELD_NAME } from '../Constants/FieldNames'
import { ChatIntegrationTypes, EmailIntegrationTypes } from '../Constants/OmnichannelTypes'
import { TabIDs } from '../Constants/TabConstants'
import { ConfigureAgentsContext, ConfigureAgentsContextType } from '../Contexts/ConfigureAgentsContext'
import { ConfigureAgentsLoader } from '../Loader/ConfigureAgentsLoader'
import { formStateToServer, serverToFormState } from '../Mappers/AgentMapper'
import { AgentsAdvancedTab } from '../Tabs/AgentsAdvancedTab'
import { AgentDetailsTab } from '../Tabs/AgentsDetailsTab'
import { AgentsOmnichannelTab } from '../Tabs/AgentsOmnichannelTab'
import { LinkedEntitiesTab } from '../../DeviceConfiguration/Tabs/LinkedEntitiesTab'
import { AgentForm } from '../Types/Types'
import { createAgent, getAgent, getPartitionForAgents, updateAgent } from '../../../NonReact/common/API/EntityAPIClient'
import { JSONResponse } from '../../../NonReact/common/Types/Types'

export const ConfigureAgentsTabs: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const {
        selectedAgent,
        filters: { telSysID, partitionID },
        licenceType: [licenceType, setLicenceType]
    }: ConfigureAgentsContextType = useContext(ConfigureAgentsContext)
    const userInfo: UserInfoContextType = useContext(UserInfoContext)
    const { displayError }: MessageContextType = useContext(MessageContext)
    const { closeModal }: ModalContextType = useContext(ModalContext)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [originalAgentNumber, setOriginalAgentNumber]: ReactState<string> = useState<string>(EMPTY_STRING)
    const [activeTab, setActiveTab]: ReactState<TabIDs> = useState<TabIDs>(TabIDs.DETAILS)
    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(false)

    const isOmnichannel: boolean = licenceType === ReportingAccessLevels.TWO_THOUSAND

    const defaultValues: MutableRefObject<AgentForm> = useRef({
        details: {
            guid: EMPTY_STRING,
            description: EMPTY_STRING,
            agentNumber: EMPTY_STRING,
            partition: partitionID ?? ALL_VALUE,
            updateItems: false
        },
        advanced: {
            callCentreLicenceType: CallCentreLicenceTypes.NONE,
            internalNumber: EMPTY_STRING,
            broadworksUserID: EMPTY_STRING,
            ciscoCommonID: EMPTY_STRING,
            oidOnTelServer: EMPTY_STRING,
            overrideMonitoring: false,
            owningDepartment: EMPTY_STRING,
            owningGroupID: EMPTY_STRING,
            webexCallingUserID: EMPTY_STRING,
            teamsUserID: EMPTY_STRING,
            userPrincipalName: EMPTY_STRING
        },
        omnichannel: {
            allowConcurrentChannels: isOmnichannel,
            chat: {
                chatIntegrationType: ChatIntegrationTypes.NONE,
                chatIDOnChatSystem: EMPTY_STRING
            },
            email: {
                emailIntegrationType: EmailIntegrationTypes.NONE,
                emailAddress: EMPTY_STRING,
                emailIDOnEmailSystem: EMPTY_STRING
            }
        }
    })

    // Form context.
    const methods: UseFormMethods<AgentForm> = useForm({
        defaultValues: defaultValues.current,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE
    })

    const { reset, handleSubmit, watch }: UseFormMethods<AgentForm> = methods

    const partitionValue: number = watch(PARTITION_FIELD_NAME)
    const agentValue: string = watch(AGENT_NUMBER_FIELD_NAME)

    const isEditAgent: boolean = isStringNotEmpty(selectedAgent ?? EMPTY_STRING)

    useEffect(() => {
        if (activeTab === TabIDs.LINKED_ENTITIES && !isEditAgent) {
            setActiveTab(TabIDs.DETAILS)
        }
    }, [activeTab, isEditAgent])

    // List of all tabs.
    let tabs: Nullable<Tab[]>

    if (!isLoading && userInfo) {
        tabs = [
            {
                ID: TabIDs.DETAILS,
                isDisplayed: true,
                tabsMenuItem: {
                    title: language.get(ConfigureAgentsLanguage.ConfigureAgents_Tabs_Details),
                    iconName: IconName.REPORT,
                    onClick: (): void => setActiveTab(TabIDs.DETAILS)
                },
                content: <AgentDetailsTab />
            },
            {
                ID: TabIDs.ADVANCED,
                isDisplayed: true,
                tabsMenuItem: {
                    title: language.get(ConfigureAgentsLanguage.ConfigureAgents_Tabs_Advanced),
                    iconName: IconName.ADMIN,
                    onClick: (): void => setActiveTab(TabIDs.ADVANCED)
                },
                content: <AgentsAdvancedTab />
            },
            {
                ID: TabIDs.LINKED_ENTITIES,
                isDisplayed: isEditAgent,
                tabsMenuItem: {
                    title: language.get(ConfigureAgentsLanguage.ConfigureAgents_Tabs_LinkedEntities),
                    iconName: IconName.REPORT,
                    onClick: (): void => setActiveTab(TabIDs.LINKED_ENTITIES)
                },
                content: (
                    <LinkedEntitiesTab
                        mode='agent'
                        filterTelSysID={telSysID}
                        filterPartitionID={partitionID}
                        entityNumber={selectedAgent ?? EMPTY_STRING}
                    />
                )
            },
            {
                ID: TabIDs.OMNICHANNEL,
                isDisplayed: isOmnichannel,
                tabsMenuItem: {
                    title: language.get(ConfigureAgentsLanguage.ConfigureAgents_Tabs_Omnichannel),
                    iconName: IconName.OMNICHANNEL,
                    onClick: (): void => setActiveTab(TabIDs.OMNICHANNEL)
                },
                content: <AgentsOmnichannelTab />
            }
        ]
    }

    const hasAgentNumberChanged: boolean = useMemo((): boolean => originalAgentNumber !== agentValue, [agentValue])

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    const closeAndRefresh = (): void => closeModal(true)

    const handleError = (e: Error): void => {
        displayError(e)
        setIsSubmitting(false)
    }

    const submitAgentsConfiguration = (state: AgentForm): void => {
        setIsSubmitting(true)

        const requestDTO: AgentDTO = formStateToServer(
            telSysID,
            state,
            hasAgentNumberChanged,
            isStringNotEmpty(selectedAgent)
        )

        if (isStringNotEmpty(selectedAgent)) {
            updateAgent(telSysID, partitionID, selectedAgent, requestDTO).then(closeAndRefresh).catch(handleError)
        } else {
            createAgent(telSysID, partitionValue, requestDTO).then(closeAndRefresh).catch(handleError)
        }
    }

    useEffect((): void => {
        if (isVarNotEmpty(telSysID) && isVarNotEmpty(partitionValue) && partitionValue !== ALL_VALUE) {
            getPartitionForAgents(telSysID, partitionValue, true)
                .then((partitionRes: JSONResponse<PartitionAdminDTO>): void => {
                    if (
                        isVarNotEmpty(partitionRes) &&
                        isStringNotEmpty(partitionRes.LicensedForRptUserType) &&
                        isMounted.current
                    ) {
                        setLicenceType(
                            (partitionRes.LicensedForRptUserType ?? ReportingAccessLevels.NONE) as ReportingAccessLevels
                        )
                    }
                })
                .catch(displayError)
        }
    }, [partitionValue])

    useEffect((): void => {
        if (isStringNotEmpty(selectedAgent)) {
            setIsLoading(true)
            getAgent(telSysID, partitionID, selectedAgent)
                .then((agent: JSONResponse<AgentDTO>): void => {
                    if (agent && isMounted.current) {
                        setOriginalAgentNumber(agent.AgentNumber ?? EMPTY_STRING)
                        reset(serverToFormState(agent))
                        setTimeout((): void => setIsLoading(false))
                    }
                })
                .catch(displayError)
        }
    }, [])

    return (
        <div className='configure-agents-tabs configuration-tabs'>
            {isLoading || isSubmitting || !userInfo ? <ConfigureAgentsLoader /> : <></>}
            <FormProvider {...methods}>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(submitAgentsConfiguration)}
                    className={isLoading || isSubmitting ? HIDE : EMPTY_STRING}
                    noValidate
                >
                    <Tabs tabs={tabs ?? []} activeTabID={activeTab} />
                    <FooterActions />
                </form>
            </FormProvider>
        </div>
    )
}
